// Agency Retention Center — 5-tab overhaul
// Tabs: Overview | At-Risk Clients | Saves | AI Next Best Action | ROI Dashboard
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import {
  Shield, TrendingUp, TrendingDown, Users, AlertTriangle,
  ArrowLeft, RefreshCw, Phone, CheckCircle, XCircle, Clock,
  Target, BarChart3, Star, Zap, Brain, DollarSign,
  Plus, X, Check, FileText, AlertCircle
} from 'lucide-react';

const BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

// ============================================================================
// DESIGN SYSTEM CONSTANTS
// ============================================================================
const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'at-risk', label: 'At-Risk Clients', icon: AlertTriangle },
  { id: 'saves', label: 'Saves', icon: Shield },
  { id: 'next-action', label: 'AI Next Best Action', icon: Brain },
  { id: 'roi', label: 'ROI Dashboard', icon: DollarSign },
];

function healthScoreColor(score) {
  if (score >= 70) return { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30' };
  if (score >= 50) return { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30' };
  if (score >= 30) return { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30' };
  return { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' };
}

function healthScoreLabel(score) {
  if (score >= 70) return 'Healthy';
  if (score >= 50) return 'At Risk';
  if (score >= 30) return 'High Risk';
  return 'Critical';
}

function priorityConfig(priority) {
  if (priority === 'critical') return { bg: 'bg-red-500/15', text: 'text-red-400', border: 'border-red-500/30', dot: 'bg-red-400' };
  if (priority === 'urgent') return { bg: 'bg-orange-500/15', text: 'text-orange-400', border: 'border-orange-500/30', dot: 'bg-orange-400' };
  return { bg: 'bg-amber-500/15', text: 'text-amber-400', border: 'border-amber-500/30', dot: 'bg-amber-400' };
}

// ============================================================================
// SHARED COMPONENTS
// ============================================================================

function Card({ children, className = '' }) {
  return (
    <div className={`bg-gray-900 border border-gray-800 rounded-lg ${className}`}>
      {children}
    </div>
  );
}

function KPICard({ title, value, subtitle, icon: Icon, trend, accent = false, loading }) {
  return (
    <Card className="p-5">
      {loading ? (
        <div className="animate-pulse space-y-2">
          <div className="h-3 bg-gray-700 rounded w-24" />
          <div className="h-8 bg-gray-700 rounded w-16" />
          <div className="h-3 bg-gray-700 rounded w-20" />
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-gray-400 uppercase tracking-wider font-medium">{title}</span>
            {Icon && <Icon size={16} className={accent ? 'text-amber-400' : 'text-gray-500'} />}
          </div>
          <div className="flex items-end gap-2 mb-1">
            <span className={`text-2xl font-bold tabular-nums ${accent ? 'text-amber-400' : 'text-white'}`}>
              {value}
            </span>
            {trend !== undefined && trend !== null && (
              <span className={`text-xs flex items-center gap-0.5 mb-1 ${
                trend > 0 ? 'text-emerald-400' : trend < 0 ? 'text-red-400' : 'text-gray-500'
              }`}>
                {trend > 0 ? <TrendingUp size={12} /> : trend < 0 ? <TrendingDown size={12} /> : null}
                {trend !== 0 && `${Math.abs(trend)}%`}
              </span>
            )}
          </div>
          {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
        </>
      )}
    </Card>
  );
}

function SectionHeader({ title, subtitle, action }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div>
        <h2 className="text-base font-semibold text-white">{title}</h2>
        {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

function Badge({ label, color = 'gray' }) {
  const colors = {
    gray: 'bg-gray-700 text-gray-300',
    amber: 'bg-amber-500/20 text-amber-400',
    red: 'bg-red-500/20 text-red-400',
    emerald: 'bg-emerald-500/20 text-emerald-400',
    orange: 'bg-orange-500/20 text-orange-400',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[color] || colors.gray}`}>
      {label}
    </span>
  );
}

function Spinner({ size = 20 }) {
  return (
    <RefreshCw size={size} className="animate-spin text-gray-500" />
  );
}

function EmptyState({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      {Icon && <Icon size={36} className="text-gray-600 mb-3" />}
      <p className="text-sm font-medium text-gray-400">{title}</p>
      {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
    </div>
  );
}

// ============================================================================
// RETENTION SCORE GAUGE
// ============================================================================
function RetentionGauge({ score, loading }) {
  const radius = 60;
  const circumference = Math.PI * radius; // half circle
  const pct = Math.min(100, Math.max(0, score || 0)) / 100;
  const dashoffset = circumference * (1 - pct);

  const scoreColor =
    score >= 75 ? '#10b981' :
    score >= 50 ? '#f59e0b' :
    '#ef4444';

  return (
    <Card className="p-6 flex flex-col items-center">
      <p className="text-xs text-gray-400 uppercase tracking-wider font-medium mb-4">Agency Retention Score</p>
      {loading ? (
        <div className="animate-pulse w-36 h-20 bg-gray-800 rounded" />
      ) : (
        <div className="relative flex flex-col items-center">
          <svg width="150" height="90" viewBox="0 0 150 90">
            {/* Track */}
            <path
              d="M 15 80 A 60 60 0 0 1 135 80"
              fill="none"
              stroke="#1f2937"
              strokeWidth="14"
              strokeLinecap="round"
            />
            {/* Value arc */}
            <path
              d="M 15 80 A 60 60 0 0 1 135 80"
              fill="none"
              stroke={scoreColor}
              strokeWidth="14"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashoffset}
              style={{ transition: 'stroke-dashoffset 0.8s ease' }}
            />
            {/* Score text */}
            <text x="75" y="72" textAnchor="middle" fontSize="26" fontWeight="700" fill="white">
              {score}
            </text>
            <text x="75" y="88" textAnchor="middle" fontSize="11" fill="#9ca3af">/ 100</text>
          </svg>
          <p className="text-xs mt-2 font-medium" style={{ color: scoreColor }}>
            {score >= 75 ? 'Strong' : score >= 50 ? 'Moderate' : 'Needs Attention'}
          </p>
        </div>
      )}
    </Card>
  );
}

// ============================================================================
// CHURN TREND AREA CHART
// ============================================================================
function ChurnTrendChart({ data, loading }) {
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 text-xs shadow-lg">
        <p className="font-semibold text-white mb-2">{d.month}</p>
        <p className="text-gray-300">Total Clients: <span className="text-white tabular-nums">{d.totalClients}</span></p>
        <p className="text-red-400">Churned: <span className="tabular-nums">{d.churned}</span></p>
        <p className="text-emerald-400">New: <span className="tabular-nums">+{d.newClients}</span></p>
      </div>
    );
  };

  return (
    <Card className="p-5">
      <SectionHeader title="Client Count & Churn Trend" subtitle="Monthly active clients with churn highlighted" />
      {loading ? (
        <div className="animate-pulse h-48 bg-gray-800 rounded" />
      ) : !data?.length ? (
        <EmptyState icon={BarChart3} title="No trend data available" />
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="totalGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="churnGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ef4444" stopOpacity={0.5} />
                <stop offset="95%" stopColor="#ef4444" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
            <XAxis dataKey="month" tick={{ fill: '#6b7280', fontSize: 11 }} />
            <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} />
            <Tooltip content={<CustomTooltip />} />
            <Area type="monotone" dataKey="totalClients" stroke="#3b82f6" fill="url(#totalGrad)" strokeWidth={2} name="Total" />
            <Area type="monotone" dataKey="churned" stroke="#ef4444" fill="url(#churnGrad)" strokeWidth={2} name="Churned" />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}

// ============================================================================
// CARRIER PLAN STABILITY SCOREBOARD
// ============================================================================
function CarrierScoreboard({ data, loading }) {
  return (
    <Card className="p-5">
      <SectionHeader title="Carrier Plan Stability" subtitle="Per-carrier churn rate and plan health" />
      {loading ? (
        <div className="animate-pulse space-y-2">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-10 bg-gray-800 rounded" />)}
        </div>
      ) : !data?.length ? (
        <EmptyState icon={Shield} title="No carrier data available" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left py-2 px-3 text-xs text-gray-400 font-medium">Carrier</th>
                <th className="text-center py-2 px-3 text-xs text-gray-400 font-medium">Clients</th>
                <th className="text-center py-2 px-3 text-xs text-gray-400 font-medium">Avg Risk</th>
                <th className="text-center py-2 px-3 text-xs text-gray-400 font-medium">Churn Rate</th>
                <th className="text-center py-2 px-3 text-xs text-gray-400 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={i} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                  <td className="py-2.5 px-3 font-medium text-white">{row.carrier}</td>
                  <td className="py-2.5 px-3 text-center text-gray-300 tabular-nums">{row.clientCount}</td>
                  <td className="py-2.5 px-3 text-center">
                    <span className={`tabular-nums font-medium ${
                      row.avgRiskScore >= 50 ? 'text-red-400' :
                      row.avgRiskScore >= 25 ? 'text-amber-400' : 'text-emerald-400'
                    }`}>{row.avgRiskScore}</span>
                  </td>
                  <td className="py-2.5 px-3 text-center tabular-nums text-gray-300">{row.churnRate}%</td>
                  <td className="py-2.5 px-3 text-center">
                    {row.unstable ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-500/20 text-red-400 text-xs font-bold rounded border border-red-500/30">
                        <AlertCircle size={10} />
                        UNSTABLE
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs font-medium rounded border border-emerald-500/30">
                        <Check size={10} />
                        Stable
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ============================================================================
// AGENT RETENTION RANKINGS (horizontal bar chart)
// ============================================================================
function AgentRankingsChart({ data, loading }) {
  const max = Math.max(...(data || []).map(d => d.avgRiskScore), 100);

  return (
    <Card className="p-5">
      <SectionHeader title="Agent Retention Rankings" subtitle="Average client risk score — lower is better" />
      {loading ? (
        <div className="animate-pulse space-y-3">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-8 bg-gray-800 rounded" />)}
        </div>
      ) : !data?.length ? (
        <EmptyState icon={Users} title="No agent data available" />
      ) : (
        <div className="space-y-3">
          {data.slice(0, 8).map((agent, i) => {
            const pct = Math.round((agent.avgRiskScore / max) * 100);
            const barColor = agent.avgRiskScore < 25 ? '#10b981' :
                             agent.avgRiskScore < 50 ? '#f59e0b' : '#ef4444';
            return (
              <div key={agent.agentId || i}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    {i === 0 && <Star size={12} className="text-amber-400 flex-shrink-0" />}
                    <span className="text-sm text-gray-300 truncate max-w-[160px]">{agent.agentName}</span>
                    <span className="text-xs text-gray-500">({agent.totalClients} clients)</span>
                  </div>
                  <span className="text-sm font-medium tabular-nums" style={{ color: barColor }}>
                    {agent.avgRiskScore}
                  </span>
                </div>
                <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${pct}%`, backgroundColor: barColor }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ============================================================================
// TAB 1: OVERVIEW
// ============================================================================
function OverviewTab() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [churnTrend, setChurnTrend] = useState([]);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [overviewRes, trendRes] = await Promise.all([
        fetch(`${BASE}/agency/retention/overview`, { credentials: 'include' }),
        fetch(`${BASE}/agency/retention/churn-trend?months=12`, { credentials: 'include' }),
      ]);
      const [od, td] = await Promise.all([overviewRes.json(), trendRes.json()]);
      if (od.ok !== false) setOverview(od);
      if (td.ok !== false) setChurnTrend(td.months || []);
    } catch (e) {
      setError('Failed to load overview data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const kpis = overview?.kpis || {};
  const carrierScoreboard = overview?.carrierScoreboard || [];
  const agentRankings = overview?.agentRankings || [];
  const retentionScore = overview?.retentionScore ?? 0;

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400 text-sm flex items-center gap-2">
          <AlertCircle size={16} />
          {error}
          <button onClick={load} className="ml-auto text-xs underline">Retry</button>
        </div>
      )}

      {/* Gauge + KPI Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="col-span-2 sm:col-span-1 lg:col-span-1">
          <RetentionGauge score={retentionScore} loading={loading} />
        </div>
        <KPICard
          title="Total Active"
          value={(kpis.totalActive ?? '--').toLocaleString?.() ?? '--'}
          subtitle="Active clients"
          icon={Users}
          loading={loading}
        />
        <KPICard
          title="At-Risk"
          value={kpis.atRiskCount ?? '--'}
          subtitle="Risk score ≥ 50"
          icon={AlertTriangle}
          loading={loading}
        />
        <KPICard
          title="Saves This Month"
          value={kpis.savesThisMonth ?? '--'}
          subtitle="Clients saved"
          icon={Shield}
          accent
          loading={loading}
        />
        <KPICard
          title="Retention Rate"
          value={`${kpis.retentionRate ?? '--'}%`}
          subtitle="Trailing 12 months"
          icon={TrendingUp}
          loading={loading}
        />
        <KPICard
          title="Revenue At Risk"
          value={kpis.revenueAtRisk != null ? `$${Math.round(kpis.revenueAtRisk / 1000)}k` : '--'}
          subtitle="Est. annual commission"
          icon={DollarSign}
          loading={loading}
        />
      </div>

      {/* Churn Trend Chart */}
      <ChurnTrendChart data={churnTrend} loading={loading} />

      {/* Carrier Scoreboard + Agent Rankings */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CarrierScoreboard data={carrierScoreboard} loading={loading} />
        <AgentRankingsChart data={agentRankings} loading={loading} />
      </div>
    </div>
  );
}

// ============================================================================
// TAB 2: AT-RISK CLIENTS
// ============================================================================
function AtRiskTab() {
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [error, setError] = useState(null);
  const [sortKey, setSortKey] = useState('healthScore');
  const [sortAsc, setSortAsc] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [filters, setFilters] = useState({ agentId: '', carrier: '', noDays: '', maxScore: '' });

  const [actionModal, setActionModal] = useState(null); // { type, clientId, clientName, agentId }
  const [actionLoading, setActionLoading] = useState(false);
  const [actionNote, setActionNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters.agentId) params.set('agent_id', filters.agentId);
      if (filters.carrier) params.set('carrier', filters.carrier);
      if (filters.noDays) params.set('no_contact_days', filters.noDays);
      const res = await fetch(`${BASE}/agency/retention/at-risk?${params}`, { credentials: 'include' });
      const data = await res.json();
      if (data.ok !== false) {
        let list = data.clients || [];
        if (filters.maxScore) list = list.filter(c => c.healthScore <= parseInt(filters.maxScore));
        setClients(list);
      }
    } catch (e) {
      setError('Failed to load at-risk clients');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const sorted = [...clients].sort((a, b) => {
    const av = a[sortKey] ?? 0;
    const bv = b[sortKey] ?? 0;
    if (typeof av === 'string') return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
    return sortAsc ? av - bv : bv - av;
  });

  const toggleSort = (key) => {
    if (sortKey === key) setSortAsc(s => !s);
    else { setSortKey(key); setSortAsc(true); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === sorted.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(sorted.map(c => c.clientId)));
  };

  const handleLogContact = async () => {
    if (!actionModal?.clientId) return;
    setActionLoading(true);
    try {
      await fetch(`${BASE}/agency/retention/log-contact`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId: actionModal.clientId, note: actionNote }),
      });
      setActionModal(null);
      setActionNote('');
      load();
    } catch (e) { /* ignore */ }
    setActionLoading(false);
  };

  const handleCreateTask = async (client, title, priority) => {
    setActionLoading(true);
    try {
      await fetch(`${BASE}/agency/retention/create-task`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId: client.clientId, agentId: client.agentId, title, priority }),
      });
      setActionModal(null);
      setActionNote('');
    } catch (e) { /* ignore */ }
    setActionLoading(false);
  };

  const uniqueAgents = [...new Map(clients.map(c => [c.agentId, c.agentName])).entries()]
    .map(([id, name]) => ({ id, name }));
  const uniqueCarriers = [...new Set(clients.map(c => c.carrier).filter(Boolean))].sort();

  const SortHeader = ({ label, dataKey, center }) => (
    <th
      onClick={() => toggleSort(dataKey)}
      className={`py-2.5 px-3 text-xs text-gray-400 font-medium cursor-pointer select-none hover:text-gray-200 whitespace-nowrap ${center ? 'text-center' : 'text-left'}`}
    >
      {label}
      {sortKey === dataKey && (
        <span className="ml-1 text-amber-400">{sortAsc ? '↑' : '↓'}</span>
      )}
    </th>
  );

  const recommendedAction = (score) => {
    if (score < 20) return 'CRITICAL — Escalate';
    if (score < 40) return 'Immediate outreach';
    if (score < 60) return 'Schedule annual review';
    return 'Monitor';
  };

  return (
    <div className="space-y-4">
      {/* Filter Bar */}
      <Card className="p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={filters.agentId}
              onChange={e => setFilters(f => ({ ...f, agentId: e.target.value }))}
              className="bg-gray-800 border border-gray-700 text-gray-300 text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:border-amber-500"
            >
              <option value="">All Agents</option>
              {uniqueAgents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <select
              value={filters.carrier}
              onChange={e => setFilters(f => ({ ...f, carrier: e.target.value }))}
              className="bg-gray-800 border border-gray-700 text-gray-300 text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:border-amber-500"
            >
              <option value="">All Carriers</option>
              {uniqueCarriers.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              value={filters.noDays}
              onChange={e => setFilters(f => ({ ...f, noDays: e.target.value }))}
              className="bg-gray-800 border border-gray-700 text-gray-300 text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:border-amber-500"
            >
              <option value="">Any last contact</option>
              <option value="30">No contact 30+ days</option>
              <option value="60">No contact 60+ days</option>
              <option value="90">No contact 90+ days</option>
            </select>
            <select
              value={filters.maxScore}
              onChange={e => setFilters(f => ({ ...f, maxScore: e.target.value }))}
              className="bg-gray-800 border border-gray-700 text-gray-300 text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:border-amber-500"
            >
              <option value="">All scores</option>
              <option value="59">Health Score &lt; 60 (At Risk)</option>
              <option value="39">Health Score &lt; 40 (High Risk)</option>
              <option value="19">Health Score &lt; 20 (Critical)</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">{clients.length} clients</span>
            <button onClick={load} className="p-1.5 rounded-lg hover:bg-gray-800 transition-colors">
              <RefreshCw size={14} className={`text-gray-400 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
        {selectedIds.size > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-800 flex items-center gap-3">
            <span className="text-xs text-gray-400">{selectedIds.size} selected</span>
            <button className="text-xs bg-amber-500/20 text-amber-400 border border-amber-500/30 px-3 py-1 rounded-lg hover:bg-amber-500/30 transition-colors">
              Assign Outreach Task
            </button>
            <button className="text-xs bg-gray-700 text-gray-300 px-3 py-1 rounded-lg hover:bg-gray-600 transition-colors">
              Send Campaign
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="text-xs text-gray-500 hover:text-gray-300"
            >
              Clear
            </button>
          </div>
        )}
      </Card>

      {/* Table */}
      <Card>
        {loading ? (
          <div className="p-8 flex items-center justify-center">
            <Spinner size={24} />
          </div>
        ) : error ? (
          <div className="p-6 text-center text-red-400 text-sm">{error}</div>
        ) : !sorted.length ? (
          <EmptyState
            icon={CheckCircle}
            title="No at-risk clients found"
            subtitle="All clients look healthy with your current filters"
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-800">
                <tr>
                  <th className="py-2.5 px-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === sorted.length && sorted.length > 0}
                      onChange={selectAll}
                      className="accent-amber-400"
                    />
                  </th>
                  <SortHeader label="Client" dataKey="clientName" />
                  <SortHeader label="Agent" dataKey="agentName" />
                  <SortHeader label="Carrier" dataKey="carrier" />
                  <SortHeader label="Health Score" dataKey="healthScore" center />
                  <SortHeader label="Last Contact" dataKey="daysSinceContact" center />
                  <SortHeader label="Risk Score" dataKey="riskScore" center />
                  <th className="py-2.5 px-3 text-left text-xs text-gray-400 font-medium">Recommended Action</th>
                  <th className="py-2.5 px-3 text-right text-xs text-gray-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(client => {
                  const hs = healthScoreColor(client.healthScore);
                  return (
                    <tr
                      key={client.clientId}
                      className="border-b border-gray-800/50 hover:bg-gray-800/20 transition-colors"
                    >
                      <td className="py-3 px-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(client.clientId)}
                          onChange={() => toggleSelect(client.clientId)}
                          className="accent-amber-400"
                        />
                      </td>
                      <td className="py-3 px-3">
                        <span className="font-medium text-white">{client.clientName || 'Unknown'}</span>
                      </td>
                      <td className="py-3 px-3 text-gray-400">{client.agentName}</td>
                      <td className="py-3 px-3 text-gray-300">{client.carrier}</td>
                      <td className="py-3 px-3 text-center">
                        <span className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-bold tabular-nums ${hs.bg} ${hs.text} border ${hs.border}`}>
                          {client.healthScore}
                          <span className="ml-1 font-normal text-xs opacity-70">{healthScoreLabel(client.healthScore)}</span>
                        </span>
                      </td>
                      <td className="py-3 px-3 text-center">
                        {client.daysSinceContact != null ? (
                          <span className={`tabular-nums text-xs ${
                            client.daysSinceContact > 90 ? 'text-red-400' :
                            client.daysSinceContact > 60 ? 'text-orange-400' :
                            client.daysSinceContact > 30 ? 'text-amber-400' : 'text-gray-300'
                          }`}>
                            {Math.round(client.daysSinceContact)}d ago
                          </span>
                        ) : (
                          <span className="text-red-400 text-xs">Never</span>
                        )}
                      </td>
                      <td className="py-3 px-3 text-center">
                        <span className={`tabular-nums text-xs font-medium ${
                          client.riskScore >= 75 ? 'text-red-400' :
                          client.riskScore >= 50 ? 'text-amber-400' : 'text-emerald-400'
                        }`}>{client.riskScore}</span>
                      </td>
                      <td className="py-3 px-3">
                        <span className={`text-xs ${
                          client.healthScore < 20 ? 'text-red-400 font-semibold' :
                          client.healthScore < 40 ? 'text-orange-400' : 'text-gray-400'
                        }`}>
                          {recommendedAction(client.healthScore)}
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setActionModal({ type: 'contact', clientId: client.clientId, clientName: client.clientName, agentId: client.agentId })}
                            className="p-1.5 rounded hover:bg-gray-700 transition-colors text-gray-400 hover:text-emerald-400"
                            title="Log Contact"
                          >
                            <Phone size={14} />
                          </button>
                          <button
                            onClick={() => handleCreateTask(client, `Follow up: ${client.clientName}`, 'high')}
                            className="p-1.5 rounded hover:bg-gray-700 transition-colors text-gray-400 hover:text-amber-400"
                            title="Assign Task"
                          >
                            <FileText size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Log Contact Modal */}
      {actionModal?.type === 'contact' && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-white">Log Contact</h3>
              <button onClick={() => setActionModal(null)} className="text-gray-400 hover:text-white">
                <X size={18} />
              </button>
            </div>
            <p className="text-sm text-gray-400 mb-4">
              Logging contact for <span className="text-white font-medium">{actionModal.clientName}</span>
            </p>
            <textarea
              value={actionNote}
              onChange={e => setActionNote(e.target.value)}
              placeholder="Add a note (optional)..."
              rows={3}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-amber-500 mb-4 resize-none"
            />
            <div className="flex gap-3">
              <button
                onClick={handleLogContact}
                disabled={actionLoading}
                className="flex-1 bg-amber-400 text-gray-950 font-semibold rounded-lg py-2 text-sm hover:bg-amber-300 transition-colors disabled:opacity-50"
              >
                {actionLoading ? 'Logging...' : 'Log Contact'}
              </button>
              <button
                onClick={() => setActionModal(null)}
                className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg text-sm hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// TAB 3: SAVES
// ============================================================================
function SavesTab() {
  const [loading, setLoading] = useState(true);
  const [saves, setSaves] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [funnel, setFunnel] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ clientId: '', outcome: 'pending', planChanged: false, notes: '', attemptedAt: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [savesRes, funnelRes] = await Promise.all([
        fetch(`${BASE}/agency/retention/saves`, { credentials: 'include' }),
        fetch(`${BASE}/agency/retention/saves/funnel`, { credentials: 'include' }),
      ]);
      const [sd, fd] = await Promise.all([savesRes.json(), funnelRes.json()]);
      if (sd.ok !== false) {
        setSaves(sd.saves || []);
        setMetrics(sd.metrics || {});
      }
      if (fd.ok !== false) setFunnel(fd.funnel || []);
    } catch (e) {
      setError('Failed to load saves data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    if (!form.clientId) return;
    setSubmitting(true);
    try {
      await fetch(`${BASE}/agency/retention/saves`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId: form.clientId,
          outcome: form.outcome,
          planChanged: form.planChanged,
          notes: form.notes,
          attemptedAt: form.attemptedAt || new Date().toISOString(),
        }),
      });
      setShowModal(false);
      setForm({ clientId: '', outcome: 'pending', planChanged: false, notes: '', attemptedAt: '' });
      load();
    } catch (e) { /* ignore */ }
    setSubmitting(false);
  };

  const outcomeConfig = {
    saved: { label: 'Saved', color: 'text-emerald-400', bg: 'bg-emerald-500/20', border: 'border-emerald-500/30' },
    lost: { label: 'Lost', color: 'text-red-400', bg: 'bg-red-500/20', border: 'border-red-500/30' },
    pending: { label: 'Pending', color: 'text-amber-400', bg: 'bg-amber-500/20', border: 'border-amber-500/30' },
  };

  const maxFunnelCount = Math.max(...(funnel || []).map(f => f.count), 1);

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400 text-sm">{error}</div>
      )}

      {/* Metrics Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KPICard title="Save Rate" value={`${metrics.saveRate ?? '--'}%`} subtitle="Saved / (saved + lost)" icon={Target} accent loading={loading} />
        <KPICard title="Saved This Month" value={metrics.savedCount ?? '--'} subtitle="Clients retained" icon={CheckCircle} loading={loading} />
        <KPICard title="Lost This Month" value={metrics.lostCount ?? '--'} subtitle="Clients churned" icon={XCircle} loading={loading} />
        <KPICard title="Revenue Protected" value={metrics.revenueProtected != null ? `$${Math.round(metrics.revenueProtected / 1000)}k` : '--'} subtitle="Est. annual commission" icon={DollarSign} loading={loading} />
      </div>

      {/* Funnel + Log Button */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-5">
          <SectionHeader title="Save Funnel" subtitle="At-risk to saved pipeline" />
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 bg-amber-400 text-gray-950 font-semibold text-sm px-4 py-2 rounded-lg hover:bg-amber-300 transition-colors"
          >
            <Plus size={15} />
            Log Save Attempt
          </button>
        </div>
        {loading ? (
          <div className="animate-pulse h-20 bg-gray-800 rounded" />
        ) : (
          <div className="flex items-end gap-2 overflow-x-auto pb-2">
            {funnel.map((stage, i) => {
              const pct = Math.round((stage.count / maxFunnelCount) * 100);
              const colors = ['#3b82f6', '#8b5cf6', '#f59e0b', '#f97316', '#10b981'];
              const color = colors[i] || '#6b7280';
              const isLast = i === funnel.length - 1;
              return (
                <React.Fragment key={stage.stage}>
                  <div className="flex-1 min-w-[100px] flex flex-col items-center gap-2">
                    <div className="text-xl font-bold tabular-nums" style={{ color }}>
                      {stage.count}
                    </div>
                    <div
                      className="w-full rounded-t-lg transition-all duration-700"
                      style={{ height: `${Math.max(8, pct * 1.2)}px`, backgroundColor: color, opacity: 0.8 }}
                    />
                    <p className="text-xs text-gray-400 text-center leading-tight">{stage.stage}</p>
                  </div>
                  {!isLast && (
                    <div className="flex items-center justify-center text-gray-600 flex-shrink-0 pb-8">→</div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </Card>

      {/* Saves Log */}
      <Card className="p-5">
        <SectionHeader title="Save Attempt Log" subtitle="All logged save attempts" />
        {loading ? (
          <div className="animate-pulse space-y-3">
            {[1, 2, 3].map(i => <div key={i} className="h-14 bg-gray-800 rounded" />)}
          </div>
        ) : !saves.length ? (
          <EmptyState icon={FileText} title="No save attempts logged yet" subtitle='Use "Log Save Attempt" to record your first save' />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-800">
                <tr>
                  <th className="text-left py-2.5 px-3 text-xs text-gray-400 font-medium">Client</th>
                  <th className="text-left py-2.5 px-3 text-xs text-gray-400 font-medium">Agent</th>
                  <th className="text-center py-2.5 px-3 text-xs text-gray-400 font-medium">Date</th>
                  <th className="text-center py-2.5 px-3 text-xs text-gray-400 font-medium">Outcome</th>
                  <th className="text-center py-2.5 px-3 text-xs text-gray-400 font-medium">Plan Changed</th>
                  <th className="text-left py-2.5 px-3 text-xs text-gray-400 font-medium">Notes</th>
                </tr>
              </thead>
              <tbody>
                {saves.map(s => {
                  const oc = outcomeConfig[s.outcome] || outcomeConfig.pending;
                  return (
                    <tr key={s.id} className="border-b border-gray-800/50 hover:bg-gray-800/20 transition-colors">
                      <td className="py-3 px-3 font-medium text-white">{s.clientName}</td>
                      <td className="py-3 px-3 text-gray-400">{s.agentName}</td>
                      <td className="py-3 px-3 text-center text-gray-400 tabular-nums text-xs">
                        {s.attemptedAt ? new Date(s.attemptedAt).toLocaleDateString() : '--'}
                      </td>
                      <td className="py-3 px-3 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${oc.bg} ${oc.color} border ${oc.border}`}>
                          {oc.label}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-center">
                        {s.planChanged ? (
                          <span className="text-emerald-400 text-xs">Yes</span>
                        ) : (
                          <span className="text-gray-600 text-xs">No</span>
                        )}
                      </td>
                      <td className="py-3 px-3 text-gray-400 text-xs max-w-[200px] truncate">{s.notes || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Log Save Attempt Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-semibold text-white">Log Save Attempt</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white">
                <X size={18} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1.5">Client ID *</label>
                <input
                  type="text"
                  placeholder="Paste client UUID..."
                  value={form.clientId}
                  onChange={e => setForm(f => ({ ...f, clientId: e.target.value }))}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-amber-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1.5">Outcome *</label>
                <select
                  value={form.outcome}
                  onChange={e => setForm(f => ({ ...f, outcome: e.target.value }))}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-amber-500"
                >
                  <option value="pending">Pending</option>
                  <option value="saved">Saved</option>
                  <option value="lost">Lost</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1.5">Date of Attempt</label>
                <input
                  type="date"
                  value={form.attemptedAt}
                  onChange={e => setForm(f => ({ ...f, attemptedAt: e.target.value }))}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-amber-500"
                />
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="plan-changed"
                  checked={form.planChanged}
                  onChange={e => setForm(f => ({ ...f, planChanged: e.target.checked }))}
                  className="accent-amber-400"
                />
                <label htmlFor="plan-changed" className="text-sm text-gray-300">Plan was changed</label>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1.5">Notes</label>
                <textarea
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="What happened? Any context..."
                  rows={3}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-amber-500 resize-none"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button
                onClick={handleSubmit}
                disabled={submitting || !form.clientId}
                className="flex-1 bg-amber-400 text-gray-950 font-semibold rounded-lg py-2 text-sm hover:bg-amber-300 transition-colors disabled:opacity-50"
              >
                {submitting ? 'Saving...' : 'Log Save Attempt'}
              </button>
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg text-sm hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// TAB 4: AI NEXT BEST ACTION
// ============================================================================
function NextActionTab() {
  const [loading, setLoading] = useState(true);
  const [actions, setActions] = useState([]);
  const [error, setError] = useState(null);
  const [creatingTask, setCreatingTask] = useState(null); // clientId being actioned

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BASE}/agency/retention/next-best-action`, { credentials: 'include' });
      const data = await res.json();
      if (data.ok !== false) setActions(data.actions || []);
    } catch (e) {
      setError('Failed to load next best actions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleTakeAction = async (action) => {
    setCreatingTask(action.clientId);
    try {
      await fetch(`${BASE}/agency/retention/create-task`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId: action.clientId,
          agentId: action.agentId,
          title: action.taskTitle,
          priority: action.priority === 'critical' ? 'urgent' : action.priority === 'urgent' ? 'high' : 'medium',
        }),
      });
    } catch (e) { /* ignore */ }
    setCreatingTask(null);
  };

  const priorityGroups = {
    critical: actions.filter(a => a.priority === 'critical'),
    urgent: actions.filter(a => a.priority === 'urgent'),
    moderate: actions.filter(a => a.priority === 'moderate'),
  };

  const groupLabels = {
    critical: { label: 'Critical', subtitle: 'Health Score < 20', icon: AlertCircle, color: 'text-red-400' },
    urgent: { label: 'Urgent', subtitle: 'Health Score 20–39', icon: AlertTriangle, color: 'text-orange-400' },
    moderate: { label: 'Schedule Review', subtitle: 'Health Score 40–59', icon: Clock, color: 'text-amber-400' },
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400 text-sm">{error}</div>
      )}

      {/* Summary Strip */}
      <div className="grid grid-cols-3 gap-4">
        {Object.entries(priorityGroups).map(([key, group]) => {
          const cfg = groupLabels[key];
          const Icon = cfg.icon;
          return (
            <Card key={key} className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Icon size={14} className={cfg.color} />
                <span className="text-xs text-gray-400 uppercase tracking-wider font-medium">{cfg.label}</span>
              </div>
              <div className={`text-2xl font-bold tabular-nums ${cfg.color}`}>{group.length}</div>
              <p className="text-xs text-gray-500 mt-0.5">{cfg.subtitle}</p>
            </Card>
          );
        })}
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map(i => (
            <Card key={i} className="p-4">
              <div className="animate-pulse space-y-2">
                <div className="h-4 bg-gray-800 rounded w-48" />
                <div className="h-3 bg-gray-800 rounded w-80" />
              </div>
            </Card>
          ))}
        </div>
      ) : !actions.length ? (
        <Card className="p-8">
          <EmptyState
            icon={CheckCircle}
            title="No urgent actions needed"
            subtitle="All clients have a Health Score of 60 or above"
          />
        </Card>
      ) : (
        <div className="space-y-6">
          {['critical', 'urgent', 'moderate'].map(priority => {
            const group = priorityGroups[priority];
            if (!group.length) return null;
            const cfg = groupLabels[priority];
            const Icon = cfg.icon;
            const pc = priorityConfig(priority);
            return (
              <div key={priority}>
                <div className="flex items-center gap-2 mb-3">
                  <Icon size={15} className={cfg.color} />
                  <h3 className={`text-sm font-semibold ${cfg.color}`}>{cfg.label}</h3>
                  <span className="text-xs text-gray-500">— {cfg.subtitle}</span>
                  <span className={`ml-auto text-xs font-bold tabular-nums ${cfg.color}`}>{group.length}</span>
                </div>
                <div className="space-y-3">
                  {group.map(action => (
                    <Card key={action.clientId} className={`p-4 border ${pc.border}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-1">
                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${pc.dot}`} />
                            <span className="font-semibold text-white text-sm">{action.clientName}</span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold tabular-nums ${pc.bg} ${pc.text} border ${pc.border}`}>
                              Score: {action.healthScore}
                            </span>
                          </div>
                          <p className="text-xs text-gray-300 font-medium ml-5 mb-1">{action.actionTitle}</p>
                          <p className="text-xs text-gray-500 ml-5">{action.action}</p>
                          <div className="flex items-center gap-4 mt-2 ml-5">
                            <span className="text-xs text-gray-500">Agent: <span className="text-gray-300">{action.agentName}</span></span>
                            <span className="text-xs text-gray-500">Carrier: <span className="text-gray-300">{action.carrier}</span></span>
                            {action.daysSinceContact != null && (
                              <span className="text-xs text-gray-500">
                                Last contact: <span className={`${action.daysSinceContact > 60 ? 'text-red-400' : 'text-gray-300'}`}>
                                  {action.daysSinceContact}d ago
                                </span>
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => handleTakeAction(action)}
                          disabled={creatingTask === action.clientId}
                          className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                            priority === 'critical'
                              ? 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30'
                              : priority === 'urgent'
                              ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30 hover:bg-orange-500/30'
                              : 'bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30'
                          } disabled:opacity-50`}
                        >
                          {creatingTask === action.clientId ? (
                            <Spinner size={12} />
                          ) : (
                            <Zap size={12} />
                          )}
                          Take Action
                        </button>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// TAB 5: ROI DASHBOARD
// ============================================================================
function ROITab() {
  const [loading, setLoading] = useState(true);
  const [roi, setRoi] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BASE}/agency/retention/roi`, { credentials: 'include' });
      const data = await res.json();
      if (data.ok !== false) setRoi(data);
    } catch (e) {
      setError('Failed to load ROI data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const fmtDollar = (n) => {
    if (n == null) return '--';
    if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `$${Math.round(n / 1000)}k`;
    return `$${n.toLocaleString()}`;
  };

  const monthlyTrend = roi?.monthlyTrend || [];

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 text-xs shadow-lg">
        <p className="font-semibold text-white mb-1">{d.month}</p>
        <p className="text-red-400">Revenue At Risk: <span className="tabular-nums">{fmtDollar(d.revenueAtRisk)}</span></p>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400 text-sm">{error}</div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <KPICard
          title="Revenue At Risk"
          value={fmtDollar(roi?.revenueAtRisk)}
          subtitle={`${roi?.atRiskCount ?? '--'} clients × $${roi?.commissionPerClient ?? 460}/yr`}
          icon={AlertTriangle}
          loading={loading}
        />
        <KPICard
          title="Revenue Protected"
          value={fmtDollar(roi?.revenueProtected)}
          subtitle={`${roi?.savedThisYear ?? '--'} saves this year`}
          icon={Shield}
          accent
          loading={loading}
        />
        <KPICard
          title="Retention ROI"
          value={roi?.roi != null ? `${roi.roi}x` : '--'}
          subtitle="Protected / outreach cost"
          icon={TrendingUp}
          loading={loading}
        />
        <KPICard
          title="Commission / Client"
          value={roi?.commissionPerClient != null ? `$${roi.commissionPerClient}` : '--'}
          subtitle="Estimated annual"
          icon={DollarSign}
          loading={loading}
        />
      </div>

      {/* Revenue At Risk Trend */}
      <Card className="p-5">
        <SectionHeader
          title="Revenue At Risk Trend"
          subtitle="Monthly estimate of at-risk commission (ideally decreasing)"
        />
        {loading ? (
          <div className="animate-pulse h-52 bg-gray-800 rounded" />
        ) : !monthlyTrend.length ? (
          <EmptyState icon={BarChart3} title="No trend data available" />
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={monthlyTrend} margin={{ top: 5, right: 5, left: -5, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
              <XAxis dataKey="month" tick={{ fill: '#6b7280', fontSize: 11 }} />
              <YAxis
                tickFormatter={v => `$${Math.round(v / 1000)}k`}
                tick={{ fill: '#6b7280', fontSize: 11 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="revenueAtRisk" radius={[4, 4, 0, 0]}>
                {monthlyTrend.map((entry, i) => (
                  <Cell key={i} fill={i === monthlyTrend.length - 1 ? '#f59e0b' : '#ef4444'} opacity={0.7 + i * 0.03} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* ROI Explanation */}
      <Card className="p-5">
        <SectionHeader title="How ROI Is Calculated" />
        <div className="space-y-3 text-sm text-gray-400">
          <div className="flex justify-between py-2 border-b border-gray-800">
            <span>Commission per client (estimated)</span>
            <span className="text-white tabular-nums">${roi?.commissionPerClient ?? 460}/year</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-800">
            <span>At-risk clients (Risk Score ≥ 50)</span>
            <span className="text-red-400 tabular-nums">{roi?.atRiskCount ?? '--'} clients</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-800">
            <span>Revenue at risk</span>
            <span className="text-red-400 tabular-nums">{fmtDollar(roi?.revenueAtRisk)}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-800">
            <span>Clients saved this year</span>
            <span className="text-emerald-400 tabular-nums">{roi?.savedThisYear ?? '--'}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-800">
            <span>Revenue protected (saved × commission)</span>
            <span className="text-emerald-400 tabular-nums">{fmtDollar(roi?.revenueProtected)}</span>
          </div>
          <div className="flex justify-between py-2">
            <span>Outreach cost (est. $15/attempt)</span>
            <span className="text-gray-300 tabular-nums">
              {roi?.savedThisYear != null ? fmtDollar(roi.savedThisYear * 15) : '--'}
            </span>
          </div>
          <div className="flex justify-between py-2 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 mt-2">
            <span className="font-semibold text-white">Retention ROI</span>
            <span className="font-bold text-amber-400 tabular-nums">{roi?.roi != null ? `${roi.roi}x` : '--'}</span>
          </div>
        </div>
      </Card>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function AgencyRetentionCenter() {
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <div className="min-h-screen bg-gray-950 py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-center gap-4">
          <Link
            to="/agency/dashboard"
            className="p-2 rounded-lg hover:bg-gray-800 transition-colors"
            title="Back to Dashboard"
          >
            <ArrowLeft size={18} className="text-gray-400" />
          </Link>
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <Shield size={20} className="text-amber-400" />
              Agency Retention Center
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">Monitor churn risk, track saves, and protect your book of business</p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 bg-gray-900 border border-gray-800 rounded-lg p-1 overflow-x-auto">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                  isActive
                    ? 'bg-amber-400 text-gray-950'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800'
                }`}
              >
                <Icon size={14} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'at-risk' && <AtRiskTab />}
        {activeTab === 'saves' && <SavesTab />}
        {activeTab === 'next-action' && <NextActionTab />}
        {activeTab === 'roi' && <ROITab />}
      </div>
    </div>
  );
}
